/**
 * PendingCoSignBanner
 * CCH Care — Supervisor pending co-sign queue (2026-05-20)
 *
 * Surfaces ADL records that are fully signed (9/9) but not yet
 * supervisor-reviewed. Renders only for users with a supervisor role
 * and only when at least one record is pending — otherwise null.
 *
 * Collapsed: shows a count + tone (late vs on-time) and the tap-to-
 * expand affordance. Expanded: lists each pending record; tapping a
 * row deep-links to that record's ADLFlowSheetPage via the URL fallback
 * the flow sheet already supports (shift_id + shift_type query params),
 * where the Review & Co-Sign CTA renders for supervisors on 9/9
 * records that aren't yet reviewed.
 *
 * Notes:
 * - Single fetch on mount. The queue's working set is small; the
 *   dashboard already does a lot of fetching on load, so we don't add
 *   polling. Future improvement: refetch when the dashboard refreshes.
 * - completed_by may come back null on every row right now (the
 *   auto-complete regression doesn't populate completed_by either),
 *   so we don't surface "completed by" attribution — patient + shift +
 *   date is enough for the supervisor to know what they're picking up.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPendingCoSign } from '../../../api/adl';
import { COLORS } from '../../../config/constants';
import TransparencyChip from '../../../components/ui/TransparencyChip';

const SUPERVISOR_ROLES = ['Care Supervisor', 'Care Admin'];

export default function PendingCoSignBanner({ role }) {
  const navigate = useNavigate();
  const [records, setRecords]   = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading]   = useState(false);

  const isSupervisor = SUPERVISOR_ROLES.includes(role);

  useEffect(() => {
    if (!isSupervisor) return;
    setLoading(true);
    getPendingCoSign(14)
      .then(d => { if (d.success) setRecords(d.records ?? []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isSupervisor]);

  // Render nothing for non-supervisors or when the queue is empty.
  // The banner is purely a "you have work to do" surface — no point
  // taking screen real estate when there isn't any.
  if (!isSupervisor || (!loading && records.length === 0)) return null;
  if (loading && records.length === 0) return null; // silent on first load

  const hasLate     = records.some(r => r.has_late_sections);
  const accent      = hasLate ? COLORS.warning      : COLORS.primary;
  const accentLight = hasLate ? COLORS.warningLight : COLORS.primaryLight;

  return (
    <div style={{
      margin: '12px 16px',
      background: accentLight,
      border: `1px solid ${accent}`,
      borderRadius: 12,
      overflow: 'hidden',
    }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: 'none',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          textAlign: 'left',
        }}
        aria-expanded={expanded}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }} aria-hidden="true">📋</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: accent }}>
              {records.length} record{records.length === 1 ? '' : 's'} pending your co-sign
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
              {hasLate
                ? 'Some include late documentation.'
                : 'All fully documented.'}
              {' '}Tap to {expanded ? 'collapse' : 'review'}.
            </div>
          </div>
        </div>
        <span style={{ fontSize: 14, color: accent }} aria-hidden="true">
          {expanded ? '▴' : '▾'}
        </span>
      </button>

      {expanded && (
        <div style={{
          borderTop: `1px solid ${accent}`,
          background: COLORS.surface,
        }}>
          {records.map(r => (
            <PendingCoSignRow
              key={r.adl_record_id}
              record={r}
              navigate={navigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PendingCoSignRow({ record, navigate }) {
  const name = `${record.patient_first_name ?? ''} ${record.patient_last_name ?? ''}`
    .trim() || `Patient ${record.patient_id}`;
  const dateStr = formatDate(record.shift_date);

  function open() {
    // Deep-link to the specific record. ADLFlowSheetPage already
    // supports this URL shape via its useParams + searchParams
    // fallback (2026-05-15 change). The Review & Co-Sign CTA
    // renders there on adjustedAllSigned && !supervisorSigned &&
    // isSupervisor — all three are true for any row in this list.
    const url = `/dashboard/adl/${record.patient_id}`
      + `?shift_id=${record.care_shift_id}`
      + `&shift_type=${record.shift_type}`;
    navigate(url);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${COLORS.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 600, color: COLORS.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {name}
        </div>
        <div style={{
          fontSize: 12, color: COLORS.textMuted, marginTop: 2,
          display: 'flex', alignItems: 'center', flexWrap: 'wrap',
          columnGap: 6, rowGap: 4,
        }}>
          <span>
            {record.shift_type} Shift &middot; {dateStr}
            {record.has_late_sections && (
              <span style={{ color: COLORS.warning, fontWeight: 600 }}>
                {' · '}
                {record.late_section_count} late
              </span>
            )}
          </span>
          {record.would_self_review && (
            <TransparencyChip
              kind="will_self_review"
              count={record.would_self_review_count}
            />
          )}
        </div>
      </div>
      <span
        style={{ fontSize: 18, color: COLORS.primary, marginLeft: 12 }}
        aria-hidden="true"
      >
        →
      </span>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  // Match the formatLocalDate convention used elsewhere — avoid
  // toISOString() / Date(dateStr) UTC drift for Eastern-time users.
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}